import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import '../styles/pricing.css';

const Pricing = () => {
  const {
    isProUser,
    plan,
    reportCount,
    freeLimit,
    resetDate,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    cancelAt,
    hasStripeCustomer,
    startCheckout,
    switchPlan,
    openPortal,
  } = UserAuth();
  const { lang } = useLanguage();
  const T = translations[lang].pricing;

  const formatDate = (date) =>
    date?.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });

  const resetDateLabel = formatDate(resetDate);
  const periodEndLabel = formatDate(currentPeriodEnd);
  // Stripe's cancel_at can precede the period end, so it wins when set.
  const accessUntilLabel = formatDate(cancelAt || currentPeriodEnd);

  const [searchParams] = useSearchParams();
  const status = searchParams.get('status'); // 'success' | 'cancel'
  const [busy, setBusy] = useState(null); // 'monthly' | 'yearly' | 'portal'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const handleSubscribe = async (planName) => {
    setError(null);
    setBusy(planName);
    try {
      await startCheckout(planName, T.redirecting);
    } catch (err) {
      console.error(err);
      // Backend refuses a second subscription when one is already active — route
      // the user to the portal to switch plans instead of a new checkout.
      const alreadySubscribed =
        err?.code === 'functions/failed-precondition' ||
        err?.details?.code === 'already-subscribed';
      if (alreadySubscribed) {
        setError(T.alreadySubscribedMsg);
      } else if (err?.code === 'unauthenticated' || err?.code === 'functions/unauthenticated') {
        setError(T.loginRequired);
      } else {
        setError(T.errorMsg);
      }
    } finally {
      // Checkout opens in a new tab now, so this tab survives and the buttons
      // must be re-enabled on success too, not just on error.
      setBusy(null);
    }
  };

  const handlePortal = async () => {
    setError(null);
    setBusy('portal');
    try {
      await openPortal(T.redirecting);
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  // In-app plan switch (monthly <-> yearly) with immediate proration. No tab
  // hop — the live users/{uid} snapshot updates `plan` once Stripe returns.
  const handleSwitch = async (planName, planLabel) => {
    setError(null);
    setNotice(null);
    if (!window.confirm(T.confirmSwitch(planLabel))) return;
    setBusy(planName);
    try {
      await switchPlan(planName);
      setNotice(T.switchSuccess);
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  const Plan = ({ planName, name, price, period, note }) => {
    const isCurrent = isProUser && plan === planName;
    return (
      <div className={`pricing-card${isCurrent ? ' pricing-card--current' : ''}`}>
        <h2 className="pricing-card-name">{name}</h2>
        <div className="pricing-card-price">
          <span className="pricing-card-amount">{price}</span>
          <span className="pricing-card-period">{period}</span>
        </div>
        {note && <p className="pricing-card-note">{note}</p>}
        <ul className="pricing-card-features">
          <li>{T.unlimitedReports}</li>
          <li>{T.everythingFree}</li>
          <li>{T.cancelAnytime}</li>
        </ul>
        {isCurrent ? (
          <button className="pricing-btn pricing-btn--current" disabled>
            {T.currentPlan}
          </button>
        ) : isProUser ? (
          // Already subscribed to the other plan — switch the existing
          // subscription in place (immediate proration), never a 2nd checkout.
          <button
            className="pricing-btn pricing-btn--subscribe"
            onClick={() => handleSwitch(planName, name)}
            disabled={busy !== null}
          >
            {T.switchPlan}
          </button>
        ) : (
          <button
            className="pricing-btn pricing-btn--subscribe"
            onClick={() => handleSubscribe(planName)}
            disabled={busy !== null}
          >
            {T.subscribe}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="pricing-page">
      <h1 className="pricing-title">{T.title}</h1>
      <p className="pricing-subtitle">{T.subtitle}</p>

      {status === 'success' && <div className="pricing-banner pricing-banner--success">{T.successMsg}</div>}
      {status === 'cancel' && <div className="pricing-banner pricing-banner--cancel">{T.cancelMsg}</div>}
      {notice && <div className="pricing-banner pricing-banner--success">{notice}</div>}
      {error && <div className="pricing-banner pricing-banner--error">{error}</div>}

      {isProUser ? (
        <>
          <p className="pricing-status">{T.proActive}</p>
          {currentPeriodEnd && (
            <p className="pricing-status-billing">
              {cancelAtPeriodEnd
                ? T.subscriptionCancelled(accessUntilLabel)
                : T.nextPayment(periodEndLabel)}
            </p>
          )}
        </>
      ) : (
        <p className="pricing-status">
          {T.freeUsage(reportCount, freeLimit)}{' '}
          <span className="pricing-status-reset">{T.resetsOn(resetDateLabel)}</span>
        </p>
      )}

      <div className="pricing-cards">
        <Plan
          planName="monthly"
          name={T.monthlyName}
          price={T.monthlyPrice}
          period={T.monthlyPeriod}
        />
        <Plan
          planName="yearly"
          name={T.yearlyName}
          price={T.yearlyPrice}
          period={T.yearlyPeriod}
          note={T.yearlyNote}
        />
      </div>

      {(isProUser || hasStripeCustomer) && (
        <button
          className="pricing-btn pricing-btn--portal"
          onClick={handlePortal}
          disabled={busy !== null}
        >
          {T.manageSubscription}
        </button>
      )}
    </div>
  );
};

export default Pricing;
